import { z } from 'zod';
import { FORM_TRANSPORT, type FormTransport } from './form-transport';

/**
 * CleanTalk's Anti-Spam API, the cloud check that sat in front of Gravity
 * Forms on the WordPress site for years. Server to server only: nothing loads
 * in the visitor's browser, so a corporate firewall never sees it. It scores
 * the sender's email and IP against CleanTalk's database and reads the
 * message itself. Enforcement is keyed off the auth key: unset means the
 * check is not run. Every failure mode is fail-open, because a lead is worth
 * more than the spam a missed check lets through.
 */
/**
 * CleanTalk's moderation endpoints, tried in order until one answers.
 * `moderate.cleantalk.org` is the documented host, but on Sep 9, 2026 its
 * pool completed a TLS handshake over IPv6 and then never replied, from both
 * Railway and a laptop, so every check timed out. The numbered hosts answer
 * in about a second. `CLEANTALK_API_URLS` (comma-separated) overrides the list.
 */
export const DEFAULT_CLEANTALK_URLS: readonly string[] = [
  'https://moderate4.cleantalk.org/api2.0',
  'https://moderate3.cleantalk.org/api2.0',
  'https://moderate.cleantalk.org/api2.0',
];
/** Per-host budget; a real answer takes 1 to 1.5 s, a hung host is abandoned at this. */
export const CLEANTALK_TIMEOUT_MS = 2_500;

export function cleantalkUrls(env: Readonly<Record<string, string | undefined>>): readonly string[] {
  const configured = (env.CLEANTALK_API_URLS ?? '')
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url.length > 0);
  return configured.length > 0 ? configured : DEFAULT_CLEANTALK_URLS;
}
/** Identifies this integration in CleanTalk's logs. */
const AGENT = 'bce-website';

export const CLEANTALK_RESULT = {
  /** CleanTalk saw a real person. */
  allowed: 'allowed',
  /** CleanTalk saw spam; `comment` says why. */
  denied: 'denied',
  /** The API was unreachable, timed out, or answered with an error. */
  unavailable: 'unavailable',
  /** No CLEANTALK_AUTH_KEY, so nothing was checked. */
  disabled: 'disabled',
} as const;
export type CleanTalkResult = (typeof CLEANTALK_RESULT)[keyof typeof CLEANTALK_RESULT];

export interface CleanTalkCheck {
  readonly result: CleanTalkResult;
  /** CleanTalk's own explanation, present on a denial. */
  readonly comment?: string;
}

export interface CheckCleanTalkInput {
  /** CLEANTALK_AUTH_KEY; unset means the check is not run. */
  readonly authKey: string | undefined;
  /** The validated form fields (firstName, lastName, email, phone, company, message…). */
  readonly fields: Readonly<Record<string, unknown>>;
  readonly remoteIp: string | undefined;
  readonly userAgent: string | undefined;
  /** JSON via fetch means scripts ran in the browser, which CleanTalk weighs. */
  readonly transport: FormTransport;
  /** Endpoints to try in order; defaults to `cleantalkUrls(process.env)`. */
  readonly urls?: readonly string[];
  readonly fetchImpl?: typeof fetch;
  /** Per-endpoint timeout. */
  readonly timeoutMs?: number;
}

const ResponseSchema = z
  .object({
    allow: z.union([z.number(), z.string()]).optional(),
    comment: z.string().optional(),
    errno: z.number().optional(),
    errstr: z.string().optional(),
  })
  .passthrough();

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export async function checkCleanTalk(input: CheckCleanTalkInput): Promise<CleanTalkCheck> {
  const { authKey, fields, remoteIp, userAgent, transport } = input;
  if (!authKey) return { result: CLEANTALK_RESULT.disabled };

  const fetchImpl = input.fetchImpl ?? fetch;
  const urls = input.urls ?? cleantalkUrls(process.env);
  const timeoutMs = input.timeoutMs ?? CLEANTALK_TIMEOUT_MS;
  const body = JSON.stringify({
    method_name: 'check_message',
    auth_key: authKey,
    agent: AGENT,
    sender_email: text(fields['email']),
    sender_nickname: [text(fields['firstName']), text(fields['lastName'])].filter(Boolean).join(' '),
    message: [text(fields['company']), text(fields['message'])].filter(Boolean).join('\n'),
    js_on: transport === FORM_TRANSPORT.fetch ? 1 : 0,
    ...(remoteIp ? { sender_ip: remoteIp } : {}),
    ...(userAgent ? { sender_info: { user_agent: userAgent } } : {}),
  });

  for (const url of urls) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetchImpl(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        signal: controller.signal,
        body,
      });
      const parsed = ResponseSchema.safeParse(await response.json());
      if (!parsed.success || !response.ok) {
        console.warn(JSON.stringify({ level: 'warn', event: 'cleantalk_unavailable', url, status: response.status }));
        continue;
      }
      if ((parsed.data.errno ?? 0) !== 0) {
        // The account or key is the problem, not the host: no other host will differ.
        console.warn(JSON.stringify({ level: 'warn', event: 'cleantalk_unavailable', url, errstr: parsed.data.errstr }));
        return { result: CLEANTALK_RESULT.unavailable };
      }
      if (Number(parsed.data.allow) === 1) return { result: CLEANTALK_RESULT.allowed };
      return { result: CLEANTALK_RESULT.denied, ...(parsed.data.comment ? { comment: parsed.data.comment } : {}) };
    } catch (error) {
      console.warn(JSON.stringify({ level: 'warn', event: 'cleantalk_unavailable', url }), error);
    } finally {
      clearTimeout(timer);
    }
  }
  return { result: CLEANTALK_RESULT.unavailable };
}
